import { useState, useEffect, useRef } from 'react';
import { Sparkles, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react';
import { GenreStep } from './onboarding/GenreStep';
import { MovieStep } from './onboarding/MovieStep';
import { ActorStep } from './onboarding/ActorStep';
import { DirectorStep } from './onboarding/DirectorStep';
import { useAuth } from '../contexts/AuthContext';
import type { MovieSuggestion } from '../types/onboarding';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.filmple.io';

interface SelectedMovie {
  id: number;
  title: string;
  poster_path: string | null;
  release_date: string | null;
}

interface SelectedPerson {
  id: number;
  name: string;
  profile_path: string | null;
}

interface CachedPersonSuggestion extends SelectedPerson {
  reason?: string;
}

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

export function OnboardingModal({ isOpen, onComplete }: OnboardingModalProps) {
  const { session } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selections state
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [selectedMovies, setSelectedMovies] = useState<SelectedMovie[]>([]);
  const [selectedActors, setSelectedActors] = useState<SelectedPerson[]>([]);
  const [selectedDirectors, setSelectedDirectors] = useState<SelectedPerson[]>([]);

  // Prefetch state for parallel prep
  const [prefetchedMovieSuggestions, setPrefetchedMovieSuggestions] = useState<MovieSuggestion[] | null>(null);
  const [prefetchGenres, setPrefetchGenres] = useState<string>('');
  const prefetchControllerRef = useRef<AbortController | null>(null);

  // Prefetched people (actors + directors from combined endpoint)
  const [prefetchedActors, setPrefetchedActors] = useState<SelectedPerson[] | null>(null);
  const [prefetchedDirectors, setPrefetchedDirectors] = useState<SelectedPerson[] | null>(null);
  const [peopleLoading, setPeopleLoading] = useState(false);

  // Cached suggestions (persists across back navigation)
  const [cachedMovieSuggestions, setCachedMovieSuggestions] = useState<MovieSuggestion[] | null>(null);
  const [cachedActorSuggestions, setCachedActorSuggestions] = useState<CachedPersonSuggestion[] | null>(null);
  const [cachedDirectorSuggestions, setCachedDirectorSuggestions] = useState<CachedPersonSuggestion[] | null>(null);

  // Prefetch movie suggestions while user is on genre step
  useEffect(() => {
    if (step !== 1 || selectedGenres.length === 0) {
      setPrefetchedMovieSuggestions(null);
      return;
    }

    const genresKey = selectedGenres.join(',');

    // Cancel previous prefetch
    if (prefetchControllerRef.current) {
      prefetchControllerRef.current.abort();
    }

    const controller = new AbortController();
    prefetchControllerRef.current = controller;

    // Prefetch in background
    (async () => {
      try {
        const params = new URLSearchParams({
          genres: genresKey,
          exclude: '',
          selectedMovies: '',
        });
        const response = await fetch(`${API_URL}/api/onboarding/suggestions/movies?${params}`, {
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          setPrefetchedMovieSuggestions(data.suggestions || []);
          setPrefetchGenres(genresKey);
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Prefetch failed:', err);
        }
      }
    })();

    return () => controller.abort();
  }, [step, selectedGenres.join(',')]);

  if (!isOpen) return null;

  const handleToggleGenre = (genreId: number) => {
    setSelectedGenres((prev) =>
      prev.includes(genreId) ? prev.filter((id) => id !== genreId) : [...prev, genreId]
    );
  };

  const handleAddMovie = (movie: SelectedMovie) => {
    setSelectedMovies((prev) => [...prev, movie]);
  };

  const handleRemoveMovie = (movieId: number) => {
    setSelectedMovies((prev) => prev.filter((m) => m.id !== movieId));
  };

  const handleAddActor = (actor: SelectedPerson) => {
    setSelectedActors((prev) => [...prev, actor]);
  };

  const handleRemoveActor = (actorId: number) => {
    setSelectedActors((prev) => prev.filter((a) => a.id !== actorId));
  };

  const handleAddDirector = (director: SelectedPerson) => {
    setSelectedDirectors((prev) => [...prev, director]);
  };

  const handleRemoveDirector = (directorId: number) => {
    setSelectedDirectors((prev) => prev.filter((d) => d.id !== directorId));
  };

  const handleNext = async () => {
    if (step === 2 && selectedMovies.length > 0) {
      // Moving from Movies to Actors - fetch both actors AND directors in one call
      setPeopleLoading(true);
      try {
        const params = new URLSearchParams({
          genres: selectedGenres.join(','),
          movies: selectedMovies.map((m) => m.id).join(','),
        });

        const eventSource = new EventSource(
          `${API_URL}/api/onboarding/suggestions/people/stream?${params}`
        );

        eventSource.addEventListener('complete', (event) => {
          const data = JSON.parse(event.data);
          setPrefetchedActors(
            (data.actors || []).map((a: { id: number; name: string; profile_path: string | null; reason: string }) => ({
              id: a.id,
              name: a.name,
              profile_path: a.profile_path,
              reason: a.reason,
            }))
          );
          setPrefetchedDirectors(
            (data.directors || []).map((d: { id: number; name: string; profile_path: string | null; reason: string }) => ({
              id: d.id,
              name: d.name,
              profile_path: d.profile_path,
              reason: d.reason,
            }))
          );
          eventSource.close();
          setPeopleLoading(false);
          setStep(3);
        });

        eventSource.addEventListener('error', () => {
          eventSource.close();
          setPeopleLoading(false);
          // Still advance, steps will fetch their own data
          setStep(3);
        });
      } catch (err) {
        console.error('Failed to prefetch people:', err);
        setPeopleLoading(false);
        setStep(3);
      }
    } else if (step < 4) {
      setStep((prev) => (prev + 1) as 1 | 2 | 3 | 4);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((prev) => (prev - 1) as 1 | 2 | 3 | 4);
    }
  };

  const handleSkip = async () => {
    // Skip entire onboarding - just mark as complete
    await saveOnboarding(true);
  };

  const handleComplete = async () => {
    await saveOnboarding(false);
  };

  const saveOnboarding = async (skipped: boolean) => {
    if (!session?.access_token) {
      setError('You must be logged in');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/onboarding/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          skipped,
          genres: selectedGenres,
          movies: selectedMovies.map((m) => m.id),
          actors: selectedActors.map((a) => a.id),
          directors: selectedDirectors.map((d) => d.id),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save preferences');
      }

      onComplete();
    } catch (err) {
      console.error('Failed to save onboarding:', err);
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const stepLabels = ['Genres', 'Movies', 'Actors', 'Directors'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0A0A0A]/80 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-[#0F1419] rounded-2xl w-full max-w-2xl mx-4 shadow-2xl shadow-black/50 ring-1 ring-white/[0.06] max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-white/[0.06]">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles size={24} className="text-cyan-400" />
            <h2 className="text-xl font-bold text-white">Tell us your taste</h2>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    s === step
                      ? 'bg-cyan-500 text-white'
                      : s < step
                      ? 'bg-cyan-500/30 text-cyan-400'
                      : 'bg-[#1A1F26] text-slate-500'
                  }`}
                >
                  {s}
                </div>
                {s < 4 && (
                  <div
                    className={`w-8 h-0.5 mx-1 ${
                      s < step ? 'bg-cyan-500/50' : 'bg-slate-700'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-slate-400 mt-2">{stepLabels[step - 1]}</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <GenreStep
              selectedGenres={selectedGenres}
              onToggleGenre={handleToggleGenre}
              maxSelections={3}
            />
          )}

          {step === 2 && (
            <MovieStep
              selectedGenres={selectedGenres}
              selectedMovies={selectedMovies}
              onAddMovie={handleAddMovie}
              onRemoveMovie={handleRemoveMovie}
              maxSelections={3}
              prefetchedSuggestions={
                prefetchGenres === selectedGenres.join(',') ? prefetchedMovieSuggestions : null
              }
              cachedSuggestions={cachedMovieSuggestions}
              onSuggestionsChange={setCachedMovieSuggestions}
            />
          )}

          {step === 3 && (
            <ActorStep
              selectedGenres={selectedGenres}
              selectedMovieIds={selectedMovies.map((m) => m.id)}
              selectedActors={selectedActors}
              onAddActor={handleAddActor}
              onRemoveActor={handleRemoveActor}
              maxSelections={3}
              prefetchedSuggestions={prefetchedActors}
              cachedSuggestions={cachedActorSuggestions}
              onSuggestionsChange={setCachedActorSuggestions}
            />
          )}

          {step === 4 && (
            <DirectorStep
              selectedGenres={selectedGenres}
              selectedMovieIds={selectedMovies.map((m) => m.id)}
              selectedActorIds={selectedActors.map((a) => a.id)}
              selectedDirectors={selectedDirectors}
              onAddDirector={handleAddDirector}
              onRemoveDirector={handleRemoveDirector}
              maxSelections={3}
              prefetchedSuggestions={prefetchedDirectors}
              cachedSuggestions={cachedDirectorSuggestions}
              onSuggestionsChange={setCachedDirectorSuggestions}
            />
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/[0.06]">
          <div className="flex items-center justify-between">
            {/* Left side: Back or Skip */}
            <div>
              {step > 1 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={saving}
                  className="flex items-center gap-1 px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors disabled:opacity-50"
                >
                  <ChevronLeft size={18} />
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={saving}
                  className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors disabled:opacity-50"
                >
                  Add Later
                </button>
              )}
            </div>

            {/* Right side: Next or Finish */}
            <button
              type="button"
              onClick={handleNext}
              disabled={saving || peopleLoading}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : peopleLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Finding people...
                </>
              ) : step === 4 ? (
                'Finish'
              ) : (
                <>
                  Next
                  <ChevronRight size={18} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
